/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { usePresentationStore, type TGammaStatus, type TPresentation } from "@/zapi-frontend/stores/presentation";
import { useProjectStore, type TProject } from "@/zapi-frontend/stores/project";
import $api from "@/zapi-frontend/shared/api/axios";
import { toast } from "sonner";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useModelLedger } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ModelLedgerIsland/useModelLedger";

export type TGammaStatusResponse = {
  exists: boolean;
  gammaStatus: TGammaStatus;
  gammaWebUrl: string | null;
  gammaPptxUrl: string | null;
  gammaPdfUrl: string | null;
  gammaGeneratedAt: string | null;
};

export type TSlidesStatusResponse = {
  exists: boolean;
  presentationStatus: "generating" | "completed" | "failed" | null;
};

type TPresentationSummary = NonNullable<TProject["presentation"]>;

export async function fetchGammaStatus(projectId: string): Promise<TGammaStatusResponse | null> {
  const [response, error] = await tryCatch(
    $api.get<{ data: TGammaStatusResponse }>(`/projects/${projectId}/presentations/gamma-status`)
  );

  if (error) {
    console.error("Error checking Gamma status:", error);
    return null;
  }

  const data = response?.data?.data;
  return data?.exists ? data : null;
}

export const gammaFieldsOf = (data: TGammaStatusResponse) => ({
  gammaStatus: data.gammaStatus,
  gammaWebUrl: data.gammaWebUrl,
  gammaPptxUrl: data.gammaPptxUrl,
  gammaPdfUrl: data.gammaPdfUrl,
  gammaGeneratedAt: data.gammaGeneratedAt
});

const summaryWith = (changes: Partial<TPresentationSummary>): TPresentationSummary => {
  const summary = useProjectStore.getState().selectedProject?.presentation;
  return {
    status: summary?.status ?? "completed",
    gammaWebUrl: summary?.gammaWebUrl ?? null,
    gammaPptxUrl: summary?.gammaPptxUrl ?? null,
    slidesCount: summary?.slidesCount ?? 0,
    ...changes
  };
};

const presentationOf = (data: TPresentation): TPresentation => ({
  id: data.id,
  projectId: data.projectId,
  status: data.status,
  gammaStatus: data.gammaStatus,
  gammaWebUrl: data.gammaWebUrl,
  gammaPptxUrl: data.gammaPptxUrl,
  gammaPdfUrl: data.gammaPdfUrl,
  outlineGeneratedAt: data.outlineGeneratedAt,
  outlineRefreshedAt: data.outlineRefreshedAt,
  outlineUpdatedAt: data.outlineUpdatedAt,
  gammaGeneratedAt: data.gammaGeneratedAt,
  createdAt: data.createdAt,
  allowRegeneration: data.allowRegeneration,
  slides: data.slides
});

// Takes the stored outline over the one on screen, for an outline changed outside the editor.
export async function reloadPresentation(projectId: string) {
  const [response] = await tryCatch($api.get<{ data: TPresentation }>(`/projects/${projectId}/presentations`));
  const data = response?.data?.data;
  const store = usePresentationStore.getState();
  if (!data || store.activeProjectId !== projectId) return;
  const presentation = presentationOf(data);
  store.setPresentation(presentation);
  store.setLocalPresentation(presentation);
  store.setOutlineWasRefreshed(presentation.outlineRefreshedAt != null);
  useProjectStore
    .getState()
    .updateSelectedProjectPresentation(summaryWith({ slidesCount: presentation.slides.length }));
}

// A Claude project's deck is rebuilt whenever its outline changes. The deck shown stays up while
// the new one is built, so only the status changes here.
const markDeckGenerating = (projectId: string) => {
  if (usePresentationStore.getState().activeProjectId !== projectId) return;
  usePresentationStore.setState((state) => ({
    presentation: state.presentation && { ...state.presentation, gammaStatus: "generating" },
    localPresentation: state.localPresentation && { ...state.localPresentation, gammaStatus: "generating" }
  }));
};

const DECK_FOLLOW_DELAY_MS = 3000;

// Leo starts the deck rebuild only once its turn is stored, a moment after the turn reads as finished.
export async function followDeckRefresh(projectId: string) {
  await new Promise((resolve) => setTimeout(resolve, DECK_FOLLOW_DELAY_MS));
  if (!usePresentationStore.getState().presentation) return;
  const data = await fetchGammaStatus(projectId);
  if (data?.gammaStatus === "generating") markDeckGenerating(projectId);
}

const isConflict = (error: unknown) => isAxiosError(error) && error.response?.status === 409;

// A 500 carries the raw exception text, which is not meant for users.
const serverErrorMessage = (error: unknown): string | null => {
  if (!isAxiosError(error) || error.response?.status === 500) return null;
  const message = (error.response?.data as { error?: unknown } | undefined)?.error;
  return typeof message === "string" && message.trim() ? message : null;
};

export function usePresentation(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};
  const queryClient = useQueryClient();

  const {
    presentation,
    localPresentation,
    isLoading,
    isGammaGenerating,
    isSlidesGenerating,
    isSlidesRegenerating,
    outlineWasRefreshed,
    setPresentation,
    setLocalPresentation,
    setIsLoading,
    setIsGammaGenerating,
    setIsSlidesGenerating,
    setIsSlidesRegenerating,
    setOutlineWasRefreshed,
    markClean,
    reset: resetPresentationStore,
    setActiveProject
  } = usePresentationStore();
  const { selectedProject, updateSelectedProjectPresentation } = useProjectStore();
  const { id } = useParams();
  const projectId = id;
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;
  const gammaPollingRef = useRef<NodeJS.Timeout>();
  const slidesPollingRef = useRef<NodeJS.Timeout>();
  const generateAbortRef = useRef<AbortController | null>(null);

  const [isClearing, setIsClearing] = useState(false);

  useEffect(() => {
    if (!enabled || !projectId) return;

    setActiveProject(projectId);
  }, [enabled, projectId]);

  const presentationExists = Boolean(selectedProject?.presentation);
  const slidesStatus = selectedProject?.presentation?.status || null;
  // A Claude-generated project (no template) keeps its outline through a failed deck rebuild, so
  // a failed presentation is loaded like a finished one.
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";
  const shouldFetchPresentation =
    presentationExists && (slidesStatus === "completed" || (slidesStatus === "failed" && isTemplateLess));

  async function fetchPresentation() {
    if (!projectId || isLoading || !presentationExists) return;

    setIsLoading(true);
    const [response, error] = await tryCatch($api.get<{ data: TPresentation }>(`/projects/${projectId}/presentations`));
    setIsLoading(false);

    if (error) {
      toast.error("Error loading presentation");
      return;
    }

    if (response?.data?.data) {
      const data = response.data.data;
      const presentationData = presentationOf(data);

      setPresentation(presentationData);
      setLocalPresentation(presentationData);
      setIsGammaGenerating(data.gammaStatus === "generating");

      setOutlineWasRefreshed(data.outlineRefreshedAt != null);
    }
  }

  async function checkGammaStatus() {
    if (!projectId || !usePresentationStore.getState().presentation) return;

    const data = await fetchGammaStatus(projectId);
    if (!data) return;

    // Read after the request: slides edited while it was in flight must survive.
    const { presentation: currentPresentation, localPresentation: currentLocalPresentation } =
      usePresentationStore.getState();

    if (!currentPresentation) return;

    const gammaFields = gammaFieldsOf(data);

    setPresentation({ ...currentPresentation, ...gammaFields });

    // Only update gamma fields in local presentation, preserve user's slide edits
    if (currentLocalPresentation) setLocalPresentation({ ...currentLocalPresentation, ...gammaFields });

    updateSelectedProjectPresentation(
      summaryWith({
        gammaWebUrl: data.gammaWebUrl,
        gammaPptxUrl: data.gammaPptxUrl,
        slidesCount: currentPresentation.slides.length
      })
    );

    if (data.gammaStatus === "completed") {
      setIsGammaGenerating(false);

      queryClient.invalidateQueries({
        queryKey: ["exportHistory", projectId]
      });

      if (gammaPollingRef.current) {
        clearInterval(gammaPollingRef.current);
        gammaPollingRef.current = undefined;
      }
    } else if (data.gammaStatus === "failed") {
      setIsGammaGenerating(false);
      toast.error("Preview generation failed");

      setPresentation({ ...currentPresentation, ...gammaFields, allowRegeneration: true });

      if (currentLocalPresentation)
        setLocalPresentation({ ...currentLocalPresentation, ...gammaFields, allowRegeneration: true });

      if (gammaPollingRef.current) {
        clearInterval(gammaPollingRef.current);
        gammaPollingRef.current = undefined;
      }
    }
  }

  async function checkSlidesStatus() {
    if (!projectId) return;
    if (slidesStatus !== "generating" && !isSlidesGenerating) return;

    const [response, error] = await tryCatch(
      $api.get<{ data: TSlidesStatusResponse }>(`/projects/${projectId}/presentations/status`)
    );

    if (error) {
      console.error("Error checking slides status:", error);
      return;
    }

    const data = response?.data?.data;
    if (!data) return;

    // Two polls can overlap on a slow network; only the first one to answer settles the run.
    const isStillWaiting =
      usePresentationStore.getState().isSlidesGenerating ||
      useProjectStore.getState().selectedProject?.presentation?.status === "generating";
    if (!isStillWaiting) return;

    const status = data.presentationStatus;

    if (status === "completed") {
      // The server goes on to the deck as soon as the outline is stored; the deck poll ends the run.
      setIsGammaGenerating(true);
      setIsSlidesGenerating(false);
      setIsSlidesRegenerating(false);
      markClean();
      updateSelectedProjectPresentation({
        status: "completed",
        gammaWebUrl: null,
        gammaPptxUrl: null,
        slidesCount: 0
      });

      if (slidesPollingRef.current) {
        clearInterval(slidesPollingRef.current);
        slidesPollingRef.current = undefined;
      }
    } else if (status === "failed") {
      setIsSlidesGenerating(false);
      setIsSlidesRegenerating(false);
      updateSelectedProjectPresentation(summaryWith({ status: "failed" }));
      toast.error("Slides generation failed");

      if (slidesPollingRef.current) {
        clearInterval(slidesPollingRef.current);
        slidesPollingRef.current = undefined;
      }
    }
  }

  async function generateSlides() {
    if (!projectId || isSlidesGenerating) return;

    const callerProjectId = projectId;
    setIsSlidesGenerating(true);

    if (generateAbortRef.current) generateAbortRef.current.abort();
    generateAbortRef.current = new AbortController();

    // The server answers 202 once it holds the run; the status poll brings the outline in when it lands.
    const [, error] = await tryCatch(
      $api.post(`/projects/${callerProjectId}/presentations/generate`, undefined, {
        signal: generateAbortRef.current.signal
      })
    );

    if (projectIdRef.current !== callerProjectId) return;

    if (error) {
      const isCanceled = error.name === "CanceledError" || (error as { code?: string }).code === "ERR_CANCELED";
      if (!isCanceled) toast.error(serverErrorMessage(error) ?? "Failed to generate slides");
      if (isConflict(error)) updateSelectedProjectPresentation(summaryWith({ status: "generating" }));
      setIsSlidesGenerating(false);
      return;
    }

    updateSelectedProjectPresentation({
      status: "generating",
      gammaWebUrl: null,
      gammaPptxUrl: null,
      slidesCount: 0
    });
  }

  async function generateGammaPreview() {
    if (!projectId || !presentation || isGammaGenerating) return;
    if (!presentation.slides.length) {
      toast.error("No slides to generate preview from");
      return;
    }

    const callerProjectId = projectId;
    setIsGammaGenerating(true);

    const [, error] = await tryCatch($api.post(`/projects/${callerProjectId}/presentations/regenerate-gamma`));

    if (projectIdRef.current !== callerProjectId) return;

    if (error) toast.error(serverErrorMessage(error) ?? "Failed to generate preview");

    // A conflict means a deck is already being built: follow it like one started here.
    if (error && !isConflict(error)) {
      setIsGammaGenerating(false);
      // A launch the runner path refused changed nothing on the server, so the current deck stays.
      const failedPresentation = isTemplateLess
        ? { ...presentation, allowRegeneration: true }
        : {
            ...presentation,
            gammaStatus: "failed" as const,
            gammaWebUrl: null,
            gammaPptxUrl: null,
            gammaPdfUrl: null,
            allowRegeneration: true
          };
      setPresentation(failedPresentation);
      setLocalPresentation(failedPresentation);
      return;
    }

    const updatedPresentation = {
      ...presentation,
      gammaStatus: "generating" as const,
      gammaWebUrl: null,
      gammaPptxUrl: null,
      gammaPdfUrl: null,
      allowRegeneration: false
    };
    setPresentation(updatedPresentation);
    setLocalPresentation(updatedPresentation);
  }

  async function clearPresentation() {
    if (!projectId || isClearing) return;

    setIsClearing(true);

    const [, error] = await tryCatch($api.delete(`/projects/${projectId}/presentations`));

    setIsClearing(false);

    if (error) {
      toast.error("Failed to clear presentation");
      return;
    }

    resetPresentationStore();

    updateSelectedProjectPresentation(null);

    toast.success("Presentation cleared");
  }

  useEffect(() => {
    if (!enabled) return;

    if (shouldFetchPresentation && !usePresentationStore.getState().slidesUpdatedFromChat) fetchPresentation();
  }, [enabled, shouldFetchPresentation]);

  const ledger = useModelLedger(projectId ?? "", { enabled: enabled && shouldFetchPresentation }).data;
  const ledgerSeen = useRef(ledger);
  useEffect(() => {
    const before = ledgerSeen.current;
    ledgerSeen.current = ledger;
    if (!enabled || before === undefined || before === ledger) return;
    const state = usePresentationStore.getState();
    if (state.presentation && !state.isDirty && !state.isSaving) fetchPresentation();
  }, [ledger]);

  useEffect(() => {
    if (!enabled) {
      if (slidesPollingRef.current) {
        clearInterval(slidesPollingRef.current);
        slidesPollingRef.current = undefined;
      }
      return;
    }

    if (slidesPollingRef.current) {
      clearInterval(slidesPollingRef.current);
      slidesPollingRef.current = undefined;
    }

    if (!projectId) return;
    if (slidesStatus !== "generating" && !isSlidesGenerating) return;

    const poll = () => checkSlidesStatus();

    const firstPoll = setTimeout(poll, 2000);
    slidesPollingRef.current = setInterval(poll, 5000);

    return () => {
      clearTimeout(firstPoll);
      if (slidesPollingRef.current) {
        clearInterval(slidesPollingRef.current);
        slidesPollingRef.current = undefined;
      }
    };
  }, [enabled, projectId, slidesStatus, isSlidesGenerating]);

  useEffect(() => {
    if (!enabled) {
      if (gammaPollingRef.current) {
        clearInterval(gammaPollingRef.current);
        gammaPollingRef.current = undefined;
      }
      return;
    }
    if (gammaPollingRef.current) {
      clearInterval(gammaPollingRef.current);
      gammaPollingRef.current = undefined;
    }

    if (!projectId || !presentation?.slides.length) return;

    if (presentation?.gammaStatus === "failed" || presentation?.gammaStatus === "completed") {
      setIsGammaGenerating(false);
      return;
    }

    if (presentation?.gammaStatus !== "generating") return;

    setIsGammaGenerating(true);
    const firstPoll = setTimeout(checkGammaStatus, 2000);
    gammaPollingRef.current = setInterval(checkGammaStatus, 5000);

    return () => {
      clearTimeout(firstPoll);
      if (gammaPollingRef.current) {
        clearInterval(gammaPollingRef.current);
        gammaPollingRef.current = undefined;
      }
    };
  }, [enabled, projectId, presentation?.slides.length, presentation?.gammaStatus]);

  async function regenerateSlides() {
    if (!projectId || isSlidesRegenerating || isGammaGenerating) return;

    const callerProjectId = projectId;
    setIsSlidesRegenerating(true);

    if (generateAbortRef.current) generateAbortRef.current.abort();
    generateAbortRef.current = new AbortController();

    // Answered with 202 once the server holds the run; the status poll ends the refresh.
    const [, error] = await tryCatch(
      $api.post(`/projects/${callerProjectId}/presentations/regenerate-slides`, undefined, {
        signal: generateAbortRef.current.signal
      })
    );

    if (projectIdRef.current !== callerProjectId) return;

    if (error) {
      const isCanceled = error.name === "CanceledError" || (error as { code?: string }).code === "ERR_CANCELED";
      if (!isCanceled) toast.error(serverErrorMessage(error) ?? "Failed to regenerate slides");
      if (isConflict(error)) updateSelectedProjectPresentation(summaryWith({ status: "generating" }));
      setIsSlidesRegenerating(false);
      return;
    }

    updateSelectedProjectPresentation(summaryWith({ status: "generating" }));
  }

  return {
    presentation,
    localPresentation,
    slides: localPresentation?.slides || [],

    isLoading,
    isSlidesGenerating: isSlidesGenerating || slidesStatus === "generating",
    isSlidesRegenerating,
    isGammaGenerating,
    isClearing,

    presentationExists,
    hasSlides: (localPresentation?.slides?.length || 0) > 0,
    outlineGeneratedAt: presentation?.outlineGeneratedAt || null,
    outlineUpdatedAt: localPresentation?.outlineUpdatedAt || presentation?.outlineUpdatedAt || new Date().toISOString(),
    gammaGeneratedAt: localPresentation?.gammaGeneratedAt || presentation?.gammaGeneratedAt || null,
    allowRegeneration: localPresentation?.allowRegeneration ?? presentation?.allowRegeneration ?? false,

    outlineWasRefreshed,
    isGammaFailed: presentation?.gammaStatus === "failed",
    isGammaCompleted: presentation?.gammaStatus === "completed",
    gammaPptxUrl: presentation?.gammaPptxUrl || null,
    gammaPdfUrl: presentation?.gammaPdfUrl || null,
    gammaWebUrl: presentation?.gammaWebUrl || null,

    generateSlides,
    regenerateSlides,
    clearPresentation,

    generateGammaPreview
  };
}
